import { useCallback, useEffect, useRef, useState } from 'react';
type RestoreStatus = 'loading' | 'ready' | 'missing' | 'error';
/** History stores opaque keys, never private story documents. */
export function useBrowserRoute<T>(initial: T, scope = 'local', pathFor?: (route: T) => string, options?: { ready: boolean; resolve: (hash: string) => Promise<T | null> }) {
  const [route, update] = useState(initial);
  const [state, setState] = useState<{ scope: string; status: RestoreStatus }>({ scope, status: 'loading' });
  const routes = useRef(new Map<string, T>());
  const current = useRef('');
  const observedHash = useRef('');
  const sequence = useRef(0);
  const initializedScope = useRef<string | null>(null);
  const initialRoute = useRef(initial);
  const config = useRef({ pathFor, options });
  config.current = { pathFor, options };
  const ready = options?.ready ?? true;
  useEffect(() => {
    if (!ready) return;
    let active = true;
    const changedAccount = initializedScope.current !== null && initializedScope.current !== scope;
    initializedScope.current = scope;
    routes.current.clear();
    const restore = async (hash: string, depth = 0) => {
      const request = ++sequence.current;
      observedHash.current = hash;
      setState({ scope, status: 'loading' });
      try {
        const next = config.current.options ? await config.current.options.resolve(hash) : initialRoute.current;
        if (!active || request !== sequence.current) return;
        const key = crypto.randomUUID();
        current.current = key;
        history.replaceState({ mvpRouteKey: key, mvpRouteDepth: Number.isSafeInteger(depth) && depth > 0 ? depth : 0 }, '', hash || config.current.pathFor?.(initialRoute.current));
        if (next !== null) { routes.current.set(key, next); update(next); }
        setState({ scope, status: next === null ? 'missing' : 'ready' });
      } catch {
        if (active && request === sequence.current) setState({ scope, status: 'error' });
      }
    };
    void restore(changedAccount ? config.current.pathFor?.(initialRoute.current) ?? '#/' : location.hash, changedAccount ? 0 : history.state?.mvpRouteDepth);
    const pop = (event: PopStateEvent) => {
      observedHash.current = location.hash;
      const key = event.state?.mvpRouteKey;
      if (key === current.current) return; // Landing collection owns this entry.
      if (routes.current.has(key)) {
        ++sequence.current;
        current.current = key;
        update(routes.current.get(key)!);
        setState({ scope, status: 'ready' });
      } else void restore(location.hash, event.state?.mvpRouteDepth);
    };
    const hashChange = () => { if (observedHash.current !== location.hash) void restore(location.hash, history.state?.mvpRouteDepth); };
    window.addEventListener('popstate', pop);
    window.addEventListener('hashchange', hashChange);
    return () => { active = false; ++sequence.current; window.removeEventListener('popstate', pop); window.removeEventListener('hashchange', hashChange); };
  }, [scope, ready]);
  const navigate = useCallback((next: T) => {
    ++sequence.current;
    const key = crypto.randomUUID();
    routes.current.set(key, next);
    current.current = key;
    history.pushState({ mvpRouteKey: key, mvpRouteDepth: (history.state?.mvpRouteDepth ?? 0) + 1 }, '', config.current.pathFor?.(next));
    observedHash.current = location.hash;
    update(next);
    setState({ scope, status: 'ready' });
  }, [scope]);
  const back = useCallback((fallback: T) => {
    if (history.state?.mvpRouteDepth > 0) history.back();
    else navigate(fallback);
  }, [navigate]);
  return [route, navigate, back, ready && state.scope === scope ? state.status : 'loading'] as const;
}
